import { discoverSecrets, needsCarrierKey, type SecretMap } from "./storefront-secrets.js";

/**
 * Does the deployed storefront actually serve?
 *
 * Nothing asked this before, which is the whole reason a storefront could build
 * green, deploy green, and return 500 on every catalog page. A deploy that is
 * not verified is not finished.
 *
 * Deliberately tiny — a handful of unconditional HTTP assertions covering the
 * paths a working storefront cannot fail. It is a smoke test, not a test suite.
 */

export interface ProbeResult {
  path: string;
  status: number;
  ok: boolean;
  /** Set when the body was checked for content, not just status. */
  bodyOk?: boolean;
  error?: string;
}

export type Diagnosis =
  | "carrier-key-missing"
  | "clerk-keys-missing"
  | "empty-catalog"
  | "not-deployed"
  | "unclassified"
  | "healthy";

export interface VerifyResult {
  ok: boolean;
  url: string;
  probes: ProbeResult[];
  diagnosis: Diagnosis;
  /** One line an operator can act on. */
  summary: string;
  /** Whether a deterministic repair exists for this diagnosis. */
  repairable: boolean;
}

/** Paths a working storefront cannot fail, plus what to look for in the body. */
const PROBES: Array<{ path: string; expectBody?: RegExp }> = [
  { path: "/" },
  { path: "/shop", expectBody: /·/ }, // plan names render as "Visit · Calm"
  { path: "/help" },
];

async function probe(base: string, path: string, expectBody?: RegExp): Promise<ProbeResult> {
  // Cache-bust: an edge-cached 200 would hide a broken origin, and an edge-cached
  // 500 would survive the very repair we just made.
  const url = `${base.replace(/\/$/, "")}${path}${path.includes("?") ? "&" : "?"}_v=${Date.now()}`;
  try {
    const res = await fetch(url, { redirect: "manual" });
    const status = res.status;
    // 3xx on an auth-gated path is a working app, not a failure.
    const ok = status < 400;
    if (!expectBody || !ok) return { path, status, ok };
    const body = await res.text();
    return { path, status, ok, bodyOk: expectBody.test(body) };
  } catch (e: unknown) {
    return { path, status: 0, ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Classify a failing probe set.
 *
 * The mapping is deliberately narrow. Each row corresponds to a failure that has
 * actually happened; anything else is reported as `unclassified` rather than
 * guessed at, because a confident wrong diagnosis triggers a wrong repair.
 */
export function diagnose(probes: ProbeResult[], secrets: SecretMap): Diagnosis {
  const by = (p: string) => probes.find((x) => x.path === p);
  const root = by("/");
  const shop = by("/shop");
  const help = by("/help");

  if (probes.every((p) => p.ok && p.bodyOk !== false)) return "healthy";

  // Nothing resolved at all — wrong URL, or the deploy never landed.
  if (probes.every((p) => p.status === 0)) return "not-deployed";

  // The signature of today's outage: catalog pages 500 while a page that does
  // not read the catalog is fine.
  const catalogDown = root?.status === 500 && shop?.status === 500;
  if (catalogDown && help?.ok) {
    return needsCarrierKey(secrets) ? "carrier-key-missing" : "unclassified";
  }

  // Shop renders but no plan names — upstream returned nothing sellable.
  if (shop?.ok && shop.bodyOk === false) return "empty-catalog";

  // Auth-only breakage: public pages fine, protected/auth routes erroring.
  if (root?.ok && shop?.ok && probes.some((p) => p.status >= 500)) return "clerk-keys-missing";

  if (probes.every((p) => p.status === 404)) return "not-deployed";

  return "unclassified";
}

const SUMMARIES: Record<Diagnosis, string> = {
  healthy: "All probes returned a working page.",
  "carrier-key-missing":
    "Catalog pages return 500 while non-catalog pages work — CARRIER_API_KEY is missing or invalid on the host.",
  "clerk-keys-missing": "Public pages work but auth routes error — Clerk keys are missing on the host.",
  "empty-catalog":
    "/shop renders but no plans are in it — the Carrier catalog returned nothing sellable. Not a deploy problem.",
  "not-deployed": "Nothing served at the deployed URL — the deploy did not land, or the URL is wrong.",
  unclassified: "The storefront is not serving correctly and the symptom matches no known cause.",
};

/** Only these have a deterministic fix worth attempting. */
const REPAIRABLE: ReadonlySet<Diagnosis> = new Set<Diagnosis>([
  "carrier-key-missing",
  "clerk-keys-missing",
]);

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/**
 * Probe a deployed storefront and classify the result.
 *
 * A hostname that has just been created can 404 for a few seconds before it
 * routes, so `not-deployed` is retried rather than believed first time — a
 * false "the deploy did not land" is worse than waiting. Real failures
 * (500s, an empty catalog) are conclusions, not races, so they return at once.
 */
export async function verifyStorefront(
  url: string,
  storefront: string,
  env: Record<string, string | undefined> = process.env,
  opts: { attempts?: number; delayMs?: number } = {},
): Promise<VerifyResult> {
  const attempts = Math.max(1, opts.attempts ?? 4);
  const delayMs = opts.delayMs ?? 5_000;
  const secrets = await discoverSecrets(storefront, env);

  let probes: ProbeResult[] = [];
  let diagnosis: Diagnosis = "unclassified";

  for (let attempt = 1; attempt <= attempts; attempt++) {
    probes = [];
    for (const { path, expectBody } of PROBES) {
      probes.push(await probe(url, path, expectBody));
    }
    diagnosis = diagnose(probes, secrets);
    if (diagnosis !== "not-deployed" || attempt === attempts) break;
    await sleep(delayMs);
  }

  const ok = diagnosis === "healthy";
  return {
    ok,
    url,
    probes,
    diagnosis,
    summary: SUMMARIES[diagnosis],
    repairable: !ok && REPAIRABLE.has(diagnosis),
  };
}

/** Human-readable probe line, e.g. "/shop 500". */
export function formatProbes(probes: ProbeResult[]): string {
  return probes
    .map((p) => {
      const status = p.status === 0 ? "unreachable" : String(p.status);
      const body = p.bodyOk === false ? " (no plans)" : "";
      return `${p.path} ${status}${body}`;
    })
    .join("  ");
}

/**
 * What a repair for this diagnosis needs to do.
 *
 * Returned as a description rather than executed here, so the caller owns the
 * rebuild/redeploy cycle and this module stays free of process spawning.
 */
export interface RepairPlan {
  diagnosis: Diagnosis;
  /** Re-run Clerk provisioning before rebuilding. */
  runClerk: boolean;
  /** A rebuild is required because a NEXT_PUBLIC_* value is inlined. */
  rebuild: boolean;
  note: string;
}

export function repairPlanFor(diagnosis: Diagnosis): RepairPlan | undefined {
  if (diagnosis === "carrier-key-missing") {
    return {
      diagnosis,
      runClerk: false,
      // CARRIER_API_KEY is read at request time, so re-staging and redeploying
      // is enough; no rebuild needed.
      rebuild: false,
      note: "Re-resolve CARRIER_API_KEY, stage it, and redeploy.",
    };
  }
  if (diagnosis === "clerk-keys-missing") {
    return {
      diagnosis,
      runClerk: true,
      // The publishable key is inlined at build time, so this one must rebuild.
      rebuild: true,
      note: "Provision Clerk, rebuild so the publishable key is inlined, and redeploy.",
    };
  }
  return undefined;
}

export { REPAIRABLE };
